import React, { useContext, useEffect, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Container,
    Grid,
    Typography,
    CircularProgress,
    Alert,
    AlertTitle,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Pagination,
    TextField,
    Button,
    Stack,
    Divider,
    IconButton,
    Tooltip,
    Snackbar,
    InputAdornment
} from '@mui/material';
import {
    AccountBalanceWallet,
    TrendingUp,
    CalendarToday,
    People,
    BarChart,
    List,
    History,
    Search,
    Clear,
    MonetizationOn,
    EmojiEvents,
    InfoOutlined,
    ContentCopy,
    Share,
    Link as LinkIcon
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { LoginInfo } from '../App';
import ApiFetch from './ApiFetch';
import Header from './Header';
import Footer from './Footer';
import { useTranslation } from "react-i18next";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';

const AffiliateRewardDashboard = () => {
    const { t } = useTranslation();
    const { loginInfo } = useContext(LoginInfo);
    const navigate = useNavigate();
    const location = useLocation();

    // State management
    const [loading, setLoading] = useState(true);
    const [balance, setBalance] = useState(0);
    const [statistics, setStatistics] = useState({});
    const [rewardHistory, setRewardHistory] = useState([]);
    const [balanceHistory, setBalanceHistory] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [activeTab, setActiveTab] = useState('overview');
    const [dateFilter, setDateFilter] = useState({
        startDate: '',
        endDate: ''
    });
    const [error, setError] = useState('');
    // 招待リンク関連のstate
    const [affiliateLink, setAffiliateLink] = useState('');
    const [copySuccess, setCopySuccess] = useState(false);

    // ページ読み込み時の認証チェック
    useEffect(() => {
        if (loginInfo.result === false) {
            navigate('/login');
            return;
        }
        loadDashboardData();
    }, [loginInfo, navigate]);

    // ダッシュボードデータの読み込み
    const loadDashboardData = async () => {
        setLoading(true);
        setError('');
        
        try {
            await Promise.all([
                loadBalance(),
                loadStatistics(),
                loadRewardHistory(1),
                loadBalanceTransactionHistory(1),
                // 招待リンクを生成・取得
                generateAffiliateLink()
            ]);
        } catch (error) {
            console.error('Dashboard data loading error:', error);
            setError('データの読み込みに失敗しました');
        } finally {
            setLoading(false);
        }
    };

    // 残高を取得
    const loadBalance = async () => {
        try {
            const response = await new ApiFetch().get(
                `${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/monetization/affiliateBalance`
            );
            const data = await response.json();
            if (data.result) {
                setBalance(data.balance || 0);
            }
        } catch (error) {
            console.error('Balance loading error:', error);
        }
    };

    // 統計情報を取得
    const loadStatistics = async () => {
        try {
            const response = await new ApiFetch().get(
                `${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/monetization/affiliateStatistics`
            );
            const data = await response.json();
            if (data.result) {
                setStatistics(data.statistics || {});
            }
        } catch (error) {
            console.error('Statistics loading error:', error);
        }
    };

    // 報酬履歴を取得
    const loadRewardHistory = async (page = 1, filters = {}) => {
        try {
            let url = `${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/monetization/affiliateRewardHistory?page=${page}&limit=20`;
            
            if (filters.startDate) {
                url += `&start_date=${filters.startDate}`;
            }
            if (filters.endDate) {
                url += `&end_date=${filters.endDate}`;
            }

            const response = await new ApiFetch().get(url);
            const data = await response.json();
            
            if (data.result) {
                setRewardHistory(data.rewards || []);
                setTotalPages(data.totalPages || 1);
                setCurrentPage(page);
            }
        } catch (error) {
            console.error('Reward history loading error:', error);
        }
    };

    // 残高取引履歴を取得
    const loadBalanceTransactionHistory = async (page = 1) => {
        try {
            const response = await new ApiFetch().get(
                `${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/monetization/affiliateBalanceHistory?page=${page}&limit=20`
            );
            const data = await response.json();
            
            if (data.result) {
                setBalanceHistory(data.history || []);
            }
        } catch (error) {
            console.error('Balance transaction history loading error:', error);
        }
    };

    // 招待リンクを生成・取得
    const generateAffiliateLink = async () => {
        try {
            // ユーザーのアフィリエイトコードを使用してリンクを生成
            const affiliateCode = loginInfo.displayId === "" ? loginInfo.userId : loginInfo.displayId;
            const baseUrl = window.location.origin;
            const link = `${baseUrl}/?af=${affiliateCode}`;
            setAffiliateLink(link);
            console.log('Affiliate link generated:', link);
        } catch (error) {
            console.error('Affiliate link generation error:', error);
            setAffiliateLink(`${window.location.origin}/?af=user`);
        }
    };

    // 日付フィルターの適用
    const applyDateFilter = () => {
        loadRewardHistory(1, dateFilter);
    };

    // 日付フィルターのリセット
    const resetDateFilter = () => {
        setDateFilter({ startDate: '', endDate: '' });
        loadRewardHistory(1);
    };

    // チャートデータの準備
    const prepareChartData = () => {
        if (!statistics.monthly_chart || statistics.monthly_chart.length === 0) {
            return [];
        }

        return statistics.monthly_chart.reverse().map(item => ({
            month: item.month,
            rewards: parseFloat(item.rewards)
        }));
    };

    // 金額のフォーマット
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('ja-JP', {
            style: 'currency',
            currency: 'JPY'
        }).format(amount || 0);
    };

    // 日付のフォーマット
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        
        try {
            const date = new Date(dateString);
            
            // 無効な日付をチェック
            if (isNaN(date.getTime())) {
                console.warn('Invalid date string:', dateString);
                return '-';
            }
            
            return date.toLocaleString('ja-JP', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            });
        } catch (error) {
            console.error('Date formatting error:', error, 'dateString:', dateString);
            return '-';
        }
    };

    // 報酬タイプのラベル
    const getRewardTypeLabel = (type) => {
        const types = {
            'subscription': 'サブスクリプション',
            'renewal': '更新',
            'upgrade': 'アップグレード',
            'signup': '新規登録'
        };
        return types[type] || type;
    };

    // ページネーション処理
    const handlePageChange = (page) => {
        if (activeTab === 'rewards') {
            loadRewardHistory(page, dateFilter);
        } else if (activeTab === 'balance') {
            loadBalanceTransactionHistory(page);
        }
    };

    // タブコンポーネント
    function TabPanel({ children, value, index, ...other }) {
        return (
            <div
                role="tabpanel"
                hidden={value !== index}
                id={`tabpanel-${index}`}
                aria-labelledby={`tab-${index}`}
                {...other}
            >
                {value === index && (
                    <Box sx={{ py: 3 }}>
                        {children}
                    </Box>
                )}
            </div>
        );
    }

    const chartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: '過去6ヶ月の報酬推移'
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: function(value) {
                        return '¥' + value.toLocaleString();
                    }
                }
            }
        }
    };

    // 招待リンクのコピー
    const handleCopyInviteLink = () => {
        navigator.clipboard.writeText(affiliateLink)
            .then(() => {
                setCopySuccess(true);
            })
            .catch((error) => {
                console.error('Failed to copy invite link:', error);
            });
    };

    // Snackbarクローズハンドラー
    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setCopySuccess(false);
    };

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="400px"
            >
                <CircularProgress size={60} />
            </Box>
        );
    }

    return (
        <>
            <Header />
            <Container maxWidth="xl" sx={{ mt: 4, mb: 5 }}>
                {/* ヘッダー */}
                <Box mb={4}>
                    <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MonetizationOn color="primary" />
                        アフィリエイト報酬ダッシュボード
                    </Typography>
                    
                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            <AlertTitle>エラー</AlertTitle>
                            {error}
                        </Alert>
                    )}
                </Box>

                {/* タブ */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs
                        value={activeTab}
                        onChange={(event, newValue) => setActiveTab(newValue)}
                        aria-label="dashboard tabs"
                    >
                        <Tab
                            label="概要"
                            value="overview"
                            icon={<BarChart />}
                            iconPosition="start"
                        />
                        <Tab
                            label="報酬履歴"
                            value="rewards"
                            icon={<List />}
                            iconPosition="start"
                        />
                        <Tab
                            label="残高履歴"
                            value="balance"
                            icon={<History />}
                            iconPosition="start"
                        />
                    </Tabs>
                </Box>

                {/* 概要タブ */}
                <TabPanel value={activeTab} index="overview">
                    <Grid container spacing={3} mb={4}>
                        {/* 現在の残高 */}
                        <Grid item lg={3} md={6} xs={12}>
                            <Card sx={{ height: '100%', bgcolor: 'primary.50' }}>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <AccountBalanceWallet color="primary" sx={{ fontSize: 48, mb: 1 }} />
                                    <Typography variant="h6" component="h3" gutterBottom>
                                        現在の残高
                                    </Typography>
                                    <Typography variant="h4" color="primary">
                                        {formatCurrency(balance)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* 総報酬額 */}
                        <Grid item lg={3} md={6} xs={12}>
                            <Card sx={{ height: '100%', bgcolor: 'success.50' }}>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <EmojiEvents color="success" sx={{ fontSize: 48, mb: 1 }} />
                                    <Typography variant="h6" component="h3" gutterBottom>
                                        総報酬額
                                    </Typography>
                                    <Typography variant="h4" color="success.main">
                                        {formatCurrency(statistics.total_rewards)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* 今月の報酬 */}
                        <Grid item lg={3} md={6} xs={12}>
                            <Card sx={{ height: '100%', bgcolor: 'info.50' }}>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <CalendarToday color="info" sx={{ fontSize: 48, mb: 1 }} />
                                    <Typography variant="h6" component="h3" gutterBottom>
                                        今月の報酬
                                    </Typography>
                                    <Typography variant="h4" color="info.main">
                                        {formatCurrency(statistics.monthly_rewards)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* 紹介者数 */}
                        <Grid item lg={3} md={6} xs={12}>
                            <Card sx={{ height: '100%', bgcolor: 'warning.50' }}>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <People color="warning" sx={{ fontSize: 48, mb: 1 }} />
                                    <Typography variant="h6" component="h3" gutterBottom>
                                        総紹介者数
                                    </Typography>
                                    <Typography variant="h4" color="warning.main">
                                        {statistics.total_referrals || 0}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        今月: {statistics.monthly_referrals || 0}人
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* 招待リンクセクション */}
                    <Card sx={{ mb: 4, border: '2px solid #0ea5e9', borderRadius: 2 }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={3}>
                                <LinkIcon sx={{ mr: 1, color: 'primary.main', fontSize: 28 }} />
                                <Typography variant="h5" color="primary" fontWeight="bold">
                                    🔗 あなた専用の招待リンク
                                </Typography>
                            </Box>
                            
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={8}>
                                    <TextField
                                        fullWidth
                                        // label="あなたの招待リンク（このリンクをシェアして報酬を獲得）"
                                        value={affiliateLink}
                                        multiline
                                        rows={2}
                                        InputProps={{
                                            readOnly: true,
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Tooltip title="リンクをクリップボードにコピー">
                                                        <IconButton onClick={handleCopyInviteLink} edge="end" color="primary">
                                                            <ContentCopy />
                                                        </IconButton>
                                                    </Tooltip>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                bgcolor: '#f0f9ff',
                                                fontSize: '0.9rem',
                                                fontFamily: 'monospace',
                                                border: '1px solid #0ea5e9'
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: '#0369a1',
                                                fontWeight: 600
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Box display="flex" flexDirection="column" gap={2} height="100%">
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            startIcon={<ContentCopy />}
                                            onClick={handleCopyInviteLink}
                                            sx={{ 
                                                flexGrow: 1,
                                                bgcolor: '#0ea5e9',
                                                '&:hover': {
                                                    bgcolor: '#0284c7'
                                                },
                                                fontWeight: 600
                                            }}
                                        >
                                            📋 リンクをコピー
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            startIcon={<Share />}
                                            onClick={() => {
                                                if (navigator.share) {
                                                    navigator.share({
                                                        title: '🤖 アワートAIへ参加しませんか？',
                                                        text: '🎨 AI活用プラットフォーム「アワートAI」で創作活動を始めましょう！一緒にAIを使って素晴らしい作品を作りませんか？',
                                                        url: affiliateLink
                                                    });
                                                } else {
                                                    // モバイルでない場合はコピー機能をフォールバック
                                                    handleCopyInviteLink();
                                                }
                                            }}
                                            sx={{
                                                borderColor: '#0ea5e9',
                                                color: '#0ea5e9',
                                                fontWeight: 600,
                                                '&:hover': {
                                                    borderColor: '#0284c7',
                                                    color: '#0284c7',
                                                    bgcolor: '#f0f9ff'
                                                }
                                            }}
                                        >
                                            📤 友達にシェア
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>

                            <Box sx={{ mt: 3, p: 3, bgcolor: '#f0f9ff', borderRadius: 2, border: '2px solid #0ea5e9' }}>
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                    <Typography variant="h6" sx={{ color: '#0369a1', fontWeight: 700 }}>
                                        💡 招待リンク
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        sx={{
                                            bgcolor: '#0ea5e9',
                                            color: 'white',
                                            fontWeight: 700,
                                            fontSize: '0.75rem',
                                            px: 3,
                                            py: 1,
                                            borderRadius: 2,
                                            '&:hover': {
                                                bgcolor: '#0284c7',
                                                transform: 'translateY(-1px)'
                                            },
                                            transition: 'all 0.2s ease'
                                        }}
                                        onClick={() => navigate('/monetizationLP')}
                                    >
                                        📖 ランディングページを見る
                                    </Button>
                                </Box>
                                <Typography variant="body1" sx={{ color: '#075985', mb: 2, fontWeight: 600 }}>
                                    🎯 <strong>簡単3ステップで報酬を獲得！</strong>
                                </Typography>
                                <Box sx={{ pl: 2 }}>
                                    <Typography variant="body2" sx={{ color: '#075985', mb: 1.5, display: 'flex', alignItems: 'center' }}>
                                        <Box component="span" sx={{ bgcolor: '#0ea5e9', color: 'white', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2, fontSize: '0.8rem', fontWeight: 'bold' }}>1</Box>
                                        <strong>リンクをシェア：</strong>SNSや友達にあなたの招待リンクを送信
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#075985', mb: 1.5, display: 'flex', alignItems: 'center' }}>
                                        <Box component="span" sx={{ bgcolor: '#0ea5e9', color: 'white', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2, fontSize: '0.8rem', fontWeight: 'bold' }}>2</Box>
                                        <strong>登録完了で即報酬：</strong>SMS認証完了で両者に2,000pt
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#075985', display: 'flex', alignItems: 'center' }}>
                                        <Box component="span" sx={{ bgcolor: '#0ea5e9', color: 'white', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2, fontSize: '0.8rem', fontWeight: 'bold' }}>3</Box>
                                        <strong>継続報酬をゲット：</strong>被紹介者がプラン加入すると毎月あなたに報酬
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* 報酬推移グラフ */}
                    {prepareChartData().length > 0 && (
                        <Card>
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={2}>
                                    <TrendingUp sx={{ mr: 1 }} />
                                    <Typography variant="h6">
                                        報酬推移
                                    </Typography>
                                </Box>
                                <Box height={400}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={prepareChartData()}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="month" />
                                            <YAxis tickFormatter={(value) => `¥${value.toLocaleString()}`} />
                                            <RechartsTooltip formatter={(value) => [`¥${value.toLocaleString()}`, '報酬額']} />
                                            <Line type="monotone" dataKey="rewards" stroke="#2196f3" strokeWidth={3} dot={{ fill: '#2196f3' }} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    {/* コピー成功通知 */}
                    <Snackbar
                        open={copySuccess}
                        autoHideDuration={3000}
                        onClose={() => setCopySuccess(false)}
                        message="招待リンクをクリップボードにコピーしました！"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    />
                </TabPanel>

                {/* 報酬履歴タブ */}
                <TabPanel value={activeTab} index="rewards">
                    <Card>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <Box display="flex" alignItems="center">
                                    <List sx={{ mr: 1 }} />
                                    <Typography variant="h6">
                                        報酬履歴
                                    </Typography>
                                </Box>
                                
                                {/* 日付フィルター */}
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <TextField
                                        type="date"
                                        size="small"
                                        label="開始日"
                                        InputLabelProps={{ shrink: true }}
                                        value={dateFilter.startDate}
                                        onChange={(e) => setDateFilter({...dateFilter, startDate: e.target.value})}
                                    />
                                    <TextField
                                        type="date"
                                        size="small"
                                        label="終了日"
                                        InputLabelProps={{ shrink: true }}
                                        value={dateFilter.endDate}
                                        onChange={(e) => setDateFilter({...dateFilter, endDate: e.target.value})}
                                    />
                                    <Button variant="contained" onClick={applyDateFilter} startIcon={<Search />}>
                                        検索
                                    </Button>
                                    <Button variant="outlined" onClick={resetDateFilter} startIcon={<Clear />}>
                                        リセット
                                    </Button>
                                </Stack>
                            </Box>

                            {rewardHistory.length > 0 ? (
                                <>
                                    <TableContainer>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>日付</TableCell>
                                                    <TableCell>報酬額</TableCell>
                                                    <TableCell>報酬タイプ</TableCell>
                                                    <TableCell>プラン</TableCell>
                                                    <TableCell>紹介ユーザー</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {rewardHistory.map((reward, index) => (
                                                    <TableRow key={index} hover>
                                                        <TableCell>{formatDate(reward.create_datetime)}</TableCell>
                                                        <TableCell>
                                                            <Typography color="success.main" fontWeight="bold">
                                                                {formatCurrency(reward.reward_amount)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={getRewardTypeLabel(reward.reward_type)}
                                                                color="info"
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                        <TableCell>{reward.current_plan || '-'}</TableCell>
                                                        <TableCell>{reward.referred_user_name || '-'}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    
                                    {totalPages > 1 && (
                                        <Box display="flex" justifyContent="center" mt={3}>
                                            <Pagination
                                                count={totalPages}
                                                page={currentPage}
                                                onChange={(event, page) => handlePageChange(page)}
                                                color="primary"
                                            />
                                        </Box>
                                    )}
                                </>
                            ) : (
                                <Box textAlign="center" py={6}>
                                    <InfoOutlined sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                                    <Typography color="text.secondary">
                                        報酬履歴がありません
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </TabPanel>

                {/* 残高履歴タブ */}
                <TabPanel value={activeTab} index="balance">
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={3}>
                                <History sx={{ mr: 1 }} />
                                <Typography variant="h6">
                                    残高変動履歴
                                </Typography>
                            </Box>

                            {balanceHistory.length > 0 ? (
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>日付</TableCell>
                                                <TableCell>取引タイプ</TableCell>
                                                <TableCell>金額</TableCell>
                                                <TableCell>変動前残高</TableCell>
                                                <TableCell>変動後残高</TableCell>
                                                <TableCell>詳細</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {balanceHistory.map((transaction, index) => (
                                                <TableRow key={index} hover>
                                                    <TableCell>{formatDate(transaction.create_datetime)}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={transaction.transaction_type === 'EARN' ? '入金' : '出金'}
                                                            color={transaction.transaction_type === 'EARN' ? 'success' : 'error'}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            color={transaction.transaction_type === 'EARN' ? 'success.main' : 'error.main'}
                                                        >
                                                            {transaction.transaction_type === 'EARN' ? '+' : '-'}
                                                            {formatCurrency(Math.abs(transaction.amount))}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{formatCurrency(transaction.balance_before)}</TableCell>
                                                    <TableCell>{formatCurrency(transaction.balance_after)}</TableCell>
                                                    <TableCell>{transaction.description || '-'}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Box textAlign="center" py={6}>
                                    <InfoOutlined sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                                    <Typography color="text.secondary">
                                        残高履歴がありません
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </TabPanel>
            </Container>
            <Footer />
        </>
    );
};

export default AffiliateRewardDashboard;
